import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { DeviceNotificationService, NotificationFacade, RegisterFacade } from '@ridango/components';
import { AuthParametersFacade } from '@ridango/components/lib/auth/+state/auth-parameters';
import { BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';

import { take, takeUntil } from 'rxjs/operators';
import { OnboardingType, TransferAnonymousDataModalComponent } from '@ridango/widgets';
import { DeviceIdService, OnboardingService } from '@ridango/core/lib/services';
import { AccountTicketsService } from '@ridango/components/lib/account-tickets-ngrx/services/account-tickets.service';
import { ModalController } from '@ionic/angular';

@Component({
    templateUrl: './register-verification-page.component.html',
    styleUrls: ['./register-verification-page.component.scss']
})
export class RegisterVerificationPageComponent extends BaseComponent implements OnInit, OnDestroy {
    loaded$ = this.registerFacade.registerLoaded$;
    error$ = this.registerFacade.registerError$;
    private callback: string;
    private accessToken: string;
    private onboardingType = OnboardingType;
    private profileParentPageActions: string[] = this.env.ACCOUNT.ACTIONS;

    constructor(
        @Inject('ENV') private env: Env,
        @Inject(DOCUMENT) private document: Document,
        private registerFacade: RegisterFacade,
        public route: ActivatedRoute,
        private notification: NotificationFacade,
        private onboardingService: OnboardingService,
        private pushNotification: DeviceNotificationService,
        private router: Router,
        private translate: TranslateService,
        private authParams: AuthParametersFacade,
        private accountTicketsService: AccountTicketsService,
        private deviceIdService: DeviceIdService,
        private modalCtrl: ModalController
    ) {
        super();
    }

    ngOnInit() {
        if (this.env.SIGN_ON_PAGE) {
            this.handleCallbackURL();
        }
        this.loaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe((loaded) => {
            if (loaded) {
                this.error$.pipe(take(1)).subscribe((error) => {
                    if (error) {
                        this.notification.error(this.translate.instant(error), { showAfterRouteChange: true });
                        this.router.navigate([this.translate.currentLang, 'register']);
                    } else {
                        if (this.callback) {
                            const callback = this.callback;
                            this.authParams.reset();
                            this.document.location.href = `${callback}?accessToken=${this.accessToken}`;
                        } else {
                            if (this.env.DEEP_LINKING) {
                                this.onboardingService.onboardingStatus = this.onboardingType.COMPLETED;
                            }

                            if (this.env.NOTIFICATION_PAGE_USER) {
                                this.pushNotification.checkNotificationSetting();
                            }

                            this.notification.success(this.translate.instant('LOCAL_LOGIN_SUCCESS'), {
                                showAfterRouteChange: true,
                                title: this.translate.instant('LOCAL_LOGIN_SUCCESS_TITLE')
                            });

                            this.handleRedirect();
                        }
                    }
                });
            }
        });

        this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const loginWhenVerified = this.env.REGISTRATION?.LOGIN_WHEN_VERIFIED ?? true;

            if (!params) {
                this.notification.error(this.translate.instant('LOCAL_NO_VERIFICATION_TOKEN'), {
                    showAfterRouteChange: true
                });
                this.router.navigate([this.translate.currentLang, 'register']);
            } else if (loginWhenVerified) {
                this.registerFacade.verifyAccount(params.token).subscribe(() => {
                    if (this.deviceIdService.getAccessDeviceToken()) {
                        this.accountTicketsService
                            .transferAnonymousData(TransferAnonymousDataModalComponent)
                            .pipe(take(1))
                            .subscribe();
                    }
                });
            } else {
                this.registerFacade.verifyAccountWithoutLogin(params.token);
            }
        });
    }

    handleCallbackURL() {
        this.authParams.callback$.pipe(take(2)).subscribe((callback) => {
            if (callback) {
                this.callback = callback;
            }
        });
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            if (params.callback) {
                this.authParams.setCallback(params.callback);
            }
        });
        this.registerFacade.authAccessToken$.pipe(takeUntil(this.componentDestroyed$)).subscribe((token) => {
            if (token) {
                this.accessToken = token;
            }
        });
    }

    private handleRedirect() {
        let redirectPath = ['account'];

        if (this.env.DEFAULT_REDIRECT_AFTER_REGISTER) {
            redirectPath = this.env.DEFAULT_REDIRECT_AFTER_REGISTER;
        }

        if (this.shouldRedirectToNotifications) {
            redirectPath = ['account', 'notification-settings'];
        }

        this.router.navigate([this.translate.currentLang, ...redirectPath]);
    }

    private get shouldRedirectToNotifications() {
        return (
            this.env.NOTIFICATION_CHANNELS &&
            this.env.NOTIFICATION_CHANNELS.length > 0 &&
            this.profileParentPageActions.includes('notification-settings')
        );
    }
}
